import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { API_URL, queryGet } from './fetch';
import { useMe } from './auth';

enum NotificationType {
  NewMessageEvent = 'NewMessageEvent',
}

interface Notification {
  type: NotificationType;
  data: any;
  id: string;
  to: string[];
}

interface ErrorMessage {
  error: string;
  statusCode: number;
}

export const useNotificationListener = (sessionId?: string) => {
  const esRef = useRef<EventSource | null>(null);
  const notificationIdRef = useRef<string | null>(null);
  const queryClient = useQueryClient();
  const { data: me } = useMe(true);

  useEffect(() => {
    if (!me) {
      return;
    }
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }

    const eventSource = new EventSource(`${API_URL}/sse/notifications`, {
      withCredentials: true,
    });

    esRef.current = eventSource;
    eventSource.onmessage = (event) => {
      const notification: Notification | ErrorMessage = JSON.parse(event.data);
      if ('error' in notification) {
        if (esRef.current) {
          esRef.current.close();
          esRef.current = null;
        }
        return;
      }

      if (notificationIdRef.current === notification.id) {
        return;
      }
      notificationIdRef.current = notification.id;
      if (notification.type === NotificationType.NewMessageEvent) {
        if (!sessionId) {
          queryClient.invalidateQueries([
            'messages',
            'participant',
            notification.data.participantId,
            'unread',
          ]);
        } else {
          queryClient.invalidateQueries([
            'messages',
            'session',
            sessionId,
            'unread',
          ]);
        }
      }
      if (!sessionId) {
        queryClient.invalidateQueries([
          'notifications',
          'participant',
          notification.data.participantId,
        ]);
      }
    };
    return () => {
      eventSource.close();
    };
  }, [me, queryClient, sessionId]);
};

export const useGetNotificationStatus = (participantId?: string) => {
  return useQuery<boolean>(
    ['notifications', 'participant', participantId],
    queryGet(`/notifications/participant/${participantId}`),
    {
      enabled: !!participantId,
    },
  );
};
